import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, FileText, Send, Save, AlertTriangle, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { sessionService } from '../services/SessionService';
import { noteGenerationService } from '../services/NoteGenerationService';
import { Session, SESSION_TYPE_LABELS } from '../models/Session';
import {
  NOTE_TEMPLATES,
  DEFAULT_TEMPLATE_ID,
  fieldsFor,
  getTemplate,
} from '../models/noteTemplates';

type Mode = 'new' | 'edit' | 'view';

const card: React.CSSProperties = {
  backgroundColor: 'var(--color-white)',
  borderRadius: 'var(--radius-xl)',
  boxShadow: 'var(--shadow-md)',
  border: '1px solid var(--color-border)',
  padding: 'var(--spacing-6)',
};

const label: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--text-sm)',
  fontWeight: 'var(--font-semibold)',
  color: 'var(--color-text-primary)',
  marginBottom: 'var(--spacing-2)',
};

const textarea: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-3)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  fontFamily: 'var(--font-family-base)',
  fontSize: 'var(--text-sm)',
  color: 'var(--color-text-primary)',
  backgroundColor: 'var(--color-white)',
  resize: 'vertical',
};

const btnPrimary: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--spacing-2)',
  padding: 'var(--spacing-3) var(--spacing-5)',
  backgroundColor: 'var(--color-primary)',
  color: 'var(--color-white)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontWeight: 'var(--font-semibold)',
  cursor: 'pointer',
};

export const WriteNotes: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const location = useLocation();
  const routeMode = (location.state as { mode?: string } | null)?.mode;

  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [templateId, setTemplateId] = useState<string>(DEFAULT_TEMPLATE_ID);
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiFields, setAiFields] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<string | null>(null);

  // Resolve the effective mode from the route + the session's status.
  const mode: Mode = useMemo(() => {
    if (routeMode === 'view') return 'view';
    if (routeMode === 'edit') return 'edit';
    // No explicit mode: pending-notes → new; anything already submitted → view.
    if (session && session.status !== 'pending-notes') return 'view';
    return 'new';
  }, [routeMode, session]);

  const readOnly = mode === 'view';

  // Auto-dismiss the error toast.
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(id);
  }, [toast]);

  // Ask Claude to parse the dictation transcript into the template's fields.
  const runGeneration = async (s: Session, tId: string) => {
    if (!s.dictationTranscript || !s.dictationTranscript.trim()) return;
    setAiLoading(true);
    setToast(null);
    try {
      const when = s.actualStart || s.scheduledStart;
      const end = s.actualEnd || s.scheduledEnd;
      const mins = Math.max(0, Math.round((end.getTime() - when.getTime()) / 60000));
      const duration = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
      const generated = await noteGenerationService.generate(s.dictationTranscript, tId, {
        clientName: s.clientName,
        sessionDate: when.toLocaleDateString(),
        sessionTime: when.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
        duration,
        sessionType: SESSION_TYPE_LABELS[s.sessionType],
      });
      setValues(generated);
      setAiFields(new Set(Object.keys(generated).filter((k) => (generated[k] || '').trim())));
    } catch (e) {
      console.error('AI note generation failed:', e);
      setToast("VERA couldn't generate notes. You can write them manually.");
      // Fallback: keep the raw transcript in the summary field, rest empty.
      setValues({ sessionSummary: s.dictationTranscript || '' });
      setAiFields(new Set());
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      if (!user || !sessionId) return;
      setLoading(true);
      try {
        const s = await sessionService.getSession(user.companyId, sessionId);
        if (!active) return;
        if (!s) {
          setNotFound(true);
          return;
        }
        setSession(s);
        const existing = (s.noteData as Record<string, string> | null) || {};
        const startTemplate = s.noteTemplate || DEFAULT_TEMPLATE_ID;
        setTemplateId(startTemplate);
        setValues(existing);

        // Auto-generate from the dictation transcript when this is a fresh
        // note (pending-notes, not view/edit) with a transcript and no note
        // content yet.
        const noteEmpty =
          Object.keys(existing).length === 0 ||
          Object.values(existing).every((v) => !v || !String(v).trim());
        const freshReview =
          routeMode !== 'view' && routeMode !== 'edit' && s.status === 'pending-notes';
        if (freshReview && noteEmpty && s.dictationTranscript && s.dictationTranscript.trim() && active) {
          await runGeneration(s, startTemplate);
        }
      } catch (e) {
        console.error('WriteNotes load error:', e);
        setError('Could not load this session.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, sessionId]);

  const fields = fieldsFor(templateId);
  const setField = (key: string, val: string) => {
    setValues((v) => ({ ...v, [key]: val }));
    // Once the RBT edits an AI-populated field, it's theirs — drop the badge.
    setAiFields((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const handleTemplateChange = async (newId: string) => {
    if (newId === templateId) return;
    if (session?.dictationTranscript && session.dictationTranscript.trim()) {
      const regen = window.confirm(
        'Re-generate notes for this template? This will replace the current fields.'
      );
      setTemplateId(newId);
      if (regen) await runGeneration(session, newId);
    } else {
      setTemplateId(newId);
    }
  };

  const buildNoteData = (): Record<string, any> => {
    // Keep only fields relevant to the chosen template (+ common fields).
    const out: Record<string, any> = {};
    fields.forEach((f) => {
      out[f.key] = values[f.key] || '';
    });
    return out;
  };

  const handleSubmit = async () => {
    if (!user || !sessionId) return;
    setSubmitting(true);
    setError(null);
    try {
      const noteData = buildNoteData();
      if (mode === 'edit') {
        await sessionService.resubmitNotes(user.companyId, sessionId, noteData);
      } else {
        await sessionService.submitNotes(user.companyId, sessionId, noteData, templateId);
      }
      navigate('/my-sessions');
    } catch (e) {
      console.error('WriteNotes submit error:', e);
      setError('Could not submit your notes. Please try again.');
      setSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!user || !sessionId) return;
    setSubmitting(true);
    setError(null);
    try {
      // Persist transcript field content as dictation so it isn't lost;
      // note that a draft does not change status.
      await sessionService.saveDictation(user.companyId, sessionId, values.sessionSummary || '');
      navigate('/my-sessions');
    } catch (e) {
      console.error('WriteNotes save draft error:', e);
      setError('Could not save. Please try again.');
      setSubmitting(false);
    }
  };

  const title =
    mode === 'view' ? 'Session Note' : mode === 'edit' ? 'Edit & Resubmit Note' : 'Write Notes';

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <button
        onClick={() => navigate('/my-sessions')}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          background: 'none',
          border: 'none',
          color: 'var(--color-primary)',
          cursor: 'pointer',
          marginBottom: 'var(--spacing-5)',
          fontWeight: 'var(--font-medium)',
        }}
      >
        <ArrowLeft size={18} />
        Back to My Sessions
      </button>

      {toast && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--spacing-3)',
            marginBottom: 'var(--spacing-4)',
            padding: 'var(--spacing-3) var(--spacing-4)',
            backgroundColor: 'rgba(196,154,42,0.12)',
            border: '1px solid var(--color-accent)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-text-primary)',
            fontSize: 'var(--text-sm)',
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            <AlertTriangle size={16} style={{ color: 'var(--color-accent-dark)' }} />
            {toast}
          </span>
          <button
            onClick={() => setToast(null)}
            aria-label="Dismiss"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', display: 'inline-flex' }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ ...card, textAlign: 'center', color: 'var(--color-text-secondary)' }}>Loading…</div>
      ) : notFound ? (
        <div style={{ ...card, textAlign: 'center', color: 'var(--color-text-secondary)' }}>
          Session not found.
        </div>
      ) : (
        <>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}>
            <FileText size={22} style={{ color: 'var(--color-primary)' }} />
            <div>
              <h1 style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)', color: 'var(--color-text-primary)' }}>
                {title}
              </h1>
              {session && (
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                  {session.clientName}
                </div>
              )}
            </div>
          </div>

          {/* BCBA revision comments */}
          {session?.status === 'needs-revision' && session.supervisorComments && (
            <div
              style={{
                ...card,
                marginBottom: 'var(--spacing-5)',
                backgroundColor: 'rgba(239,68,68,0.06)',
                borderColor: 'rgba(239,68,68,0.3)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)' }}>
                <AlertTriangle size={16} style={{ color: '#991B1B' }} />
                <span style={{ fontWeight: 'var(--font-semibold)', color: '#991B1B' }}>BCBA requested revision</span>
              </div>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                {session.supervisorComments}
              </div>
            </div>
          )}

          {error && (
            <div style={{ ...card, marginBottom: 'var(--spacing-5)', borderColor: 'var(--color-error)', color: 'var(--color-error)' }}>
              {error}
            </div>
          )}

          {aiLoading ? (
            <div style={{ ...card, textAlign: 'center', padding: 'var(--spacing-10)' }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-light-accent)',
                  color: 'var(--color-primary)',
                  marginBottom: 'var(--spacing-4)',
                  animation: 'vera-ai-pulse 1.4s ease-in-out infinite',
                }}
              >
                <FileText size={26} />
              </div>
              <div style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text-primary)', marginBottom: '4px' }}>
                VERA is preparing your notes…
              </div>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                Parsing your dictation into the {getTemplate(templateId).label} template.
              </div>
              <style>{`@keyframes vera-ai-pulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.08); opacity: 0.6; } }`}</style>
            </div>
          ) : (
          <>
          <div style={{ ...card, display: 'grid', gap: 'var(--spacing-5)' }}>
            {/* Template picker (hidden in read-only) */}
            <div>
              <label style={label}>Note Template</label>
              {readOnly ? (
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                  {getTemplate(templateId).label} — {getTemplate(templateId).description}
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
                  {NOTE_TEMPLATES.map((t) => {
                    const active = t.id === templateId;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => handleTemplateChange(t.id)}
                        title={t.description}
                        style={{
                          padding: 'var(--spacing-2) var(--spacing-4)',
                          borderRadius: 'var(--radius-md)',
                          border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
                          backgroundColor: active ? 'var(--color-primary)' : 'var(--color-white)',
                          color: active ? 'var(--color-white)' : 'var(--color-text-primary)',
                          fontWeight: 'var(--font-medium)',
                          fontSize: 'var(--text-sm)',
                          cursor: 'pointer',
                        }}
                      >
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Fields */}
            {fields.map((f) => (
              <div key={f.key}>
                <label style={label} htmlFor={f.key}>
                  {f.label}
                  {!readOnly && aiFields.has(f.key) && (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        marginLeft: '8px',
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-medium)',
                        color: 'var(--color-primary)',
                        backgroundColor: 'var(--color-light-accent)',
                        borderRadius: '999px',
                        padding: '1px 8px',
                      }}
                    >
                      <FileText size={11} /> AI-generated
                    </span>
                  )}
                </label>
                {readOnly ? (
                  <div
                    style={{
                      fontSize: 'var(--text-sm)',
                      color: values[f.key] ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                      whiteSpace: 'pre-wrap',
                      padding: 'var(--spacing-2) 0',
                    }}
                  >
                    {values[f.key] || '—'}
                  </div>
                ) : (
                  <textarea
                    id={f.key}
                    style={textarea}
                    rows={f.rows || 3}
                    placeholder={f.placeholder}
                    value={values[f.key] || ''}
                    onChange={(e) => setField(f.key, e.target.value)}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Actions */}
          {!readOnly && (
            <div style={{ display: 'flex', gap: 'var(--spacing-3)', marginTop: 'var(--spacing-5)', flexWrap: 'wrap' }}>
              <button style={{ ...btnPrimary, opacity: submitting ? 0.6 : 1 }} onClick={handleSubmit} disabled={submitting}>
                <Send size={16} />
                {mode === 'edit' ? 'Resubmit for Review' : 'Submit for Review'}
              </button>
              {mode === 'new' && (
                <button
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-2)',
                    padding: 'var(--spacing-3) var(--spacing-4)',
                    background: 'transparent',
                    color: 'var(--color-primary)',
                    border: '1px solid var(--color-primary)',
                    borderRadius: 'var(--radius-md)',
                    fontWeight: 'var(--font-medium)',
                    cursor: 'pointer',
                    opacity: submitting ? 0.6 : 1,
                  }}
                  onClick={handleSaveDraft}
                  disabled={submitting}
                >
                  <Save size={16} />
                  Save &amp; Finish Later
                </button>
              )}
            </div>
          )}
          </>
          )}
        </>
      )}
    </div>
  );
};

export default WriteNotes;
